import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogTrigger
} from '../ui/dialog';
import { 
  Upload, 
  FileText, 
  XCircle, 
  Clock
} from 'lucide-react';

/**
 * ImportCollectionModal - Import Postman/OpenAPI Collections
 * 
 * Handles file upload via drag-and-drop or file picker
 * for importing API collections.
 */
export const ImportCollectionModal = ({
  // Modal state
  open,
  onOpenChange,
  // Drag and drop state
  dragActive,
  onDrop,
  onDragOver,
  onDragLeave,
  // File state
  selectedFile,
  onFileSelect,
  onClearFile,
  // Actions
  onImport,
  importLoading,
  // Trigger (optional)
  trigger
}) => {
  const { t } = useApiTestingLanguage();
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onFileSelect(e.target.files[0]);
    }
  };

  const modalContent = (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t("Import API Collection")}</DialogTitle>
        <DialogDescription>
          {t("Import tests from Postman collections, OpenAPI specs, or cURL commands.")}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-6">
        {/* File Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          data-testid="import-dropzone"
        >
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <div className="space-y-2">
            <p className="text-lg font-medium text-gray-900">
              {t("Drop your file here or click to browse")}
            </p>
            <p className="text-sm text-gray-600">
              {t("Supports Postman Collection v2.1 (JSON) and OpenAPI 3.0 (JSON/YAML)")}
            </p>
            <p className="text-xs text-gray-500">
              {t("Maximum file size: 10MB | Up to 1,000 endpoints")}
            </p>
          </div>
          <input
            type="file"
            accept=".json,.yaml,.yml"
            onChange={handleFileInputChange}
            className="hidden"
            id="import-file-upload"
            data-testid="import-file-input"
          />
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => document.getElementById('import-file-upload').click()}
            data-testid="import-choose-file-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            {t("Choose File")}
          </Button>
        </div>

        {/* Selected File Info */}
        {selectedFile && (
          <div className="bg-blue-50 p-4 rounded-lg" data-testid="import-selected-file">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900">{selectedFile.name}</p>
                <p className="text-sm text-blue-700">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFile}
                data-testid="import-clear-file-btn"
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Upload Button */}
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onClearFile();
            }}
            data-testid="import-cancel-btn"
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={onImport}
            disabled={!selectedFile || importLoading}
            className="bg-gradient-to-r from-blue-600 to-purple-600"
            data-testid="import-submit-btn"
          >
            {importLoading ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                {t("Processing...")}
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {t("Import")}
              </>
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  );

  // If trigger is provided, use it with DialogTrigger
  if (trigger) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
        {modalContent}
      </Dialog>
    );
  }

  // Otherwise, just use Dialog without trigger
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {modalContent}
    </Dialog>
  );
};

/**
 * ImportResultsModal - Display Import Results Summary
 * 
 * Shows detailed results after importing an API collection
 * including statistics, skipped items, and next steps.
 */
export const ImportResultsModal = ({
  // Modal state
  open,
  onOpenChange,
  // Import result data
  importResult,
  // Actions
  onViewTests,
  onDone
}) => {
  const { t } = useApiTestingLanguage();
  if (!importResult) return null;

  const summary = importResult.report?.summary;
  const skippedItems = importResult.report?.skipped_items || [];
  const isFailed = importResult.import_job?.status === 'failed';
  const errors = importResult.import_job?.errors || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Import Results")}</DialogTitle>
          <DialogDescription>
            {t("Summary of imported API tests from your collection.")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Import Summary - Success */}
          {importResult.report && !isFailed && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{t("Import Successful!")}</h3>
                  <p className="text-gray-600">{t("Your API collection has been imported")}</p>
                </div>
              </div>

              {/* Statistics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-3xl font-bold text-blue-600">
                    {summary?.total_endpoints || 0}
                  </p>
                  <p className="text-sm text-gray-600">{t("Total Endpoints")}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-3xl font-bold text-green-600">
                    {summary?.created_tests || 0}
                  </p>
                  <p className="text-sm text-gray-600">{t("Tests Created")}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-3xl font-bold text-purple-600">
                    {summary?.created_environments || 0}
                  </p>
                  <p className="text-sm text-gray-600">{t("Environments")}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-3xl font-bold text-orange-600">
                    {summary?.skipped_endpoints || 0}
                  </p>
                  <p className="text-sm text-gray-600">{t("Skipped")}</p>
                </div>
              </div>

              {/* Format and File Info */}
              <div className="mt-4 flex items-center space-x-4 text-sm text-gray-600">
                <Badge variant="outline">
                  {summary?.format?.replace('_', ' ') || t("Unknown Format")}
                </Badge>
                <span>•</span>
                <span>{summary?.filename}</span>
              </div>
            </div>
          )}

          {/* Error Display */}
          {isFailed && (
            <div className="border border-red-200 bg-red-50 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">{t("Import Failed")}</p>
                  <div className="mt-2 space-y-1">
                    {errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-700">• {error}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Skipped Items */}
          {skippedItems.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center">
                <svg className="h-4 w-4 text-orange-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {t("Skipped Items (")}{skippedItems.length})
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {skippedItems.map((item, index) => (
                  <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded">
                    <p className="font-medium text-orange-900">{item.item}</p>
                    <p className="text-sm text-orange-700">{item.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          {!isFailed && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">{t("Next Steps")}</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p>{t("• Your imported tests are now available in the Tests tab")}</p>
                <p>{t("• Environment variables have been created for your API endpoints")}</p>
                <p>{t("• Configure credentials in the Credentials tab if your APIs require authentication")}</p>
                <p>{t("• Run individual tests or execute them in batches")}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            {!isFailed && (
              <Button
                variant="outline"
                onClick={onViewTests}
                data-testid="import-view-tests-btn"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                {t("View Tests")}
              </Button>
            )}
            <Button
              onClick={onDone}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              data-testid="import-done-btn"
            >
              {t("Done")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportCollectionModal;
